// Provenance + defensive-render helpers for the persisted cross-group ("answer
// across all groups") result. A stored answer must never be shown as if it
// belongs to the current search unless its matched set is identical.

// Stable fingerprint of a matched set: dedupe + sort record_ids, then FNV-1a
// (32-bit) hex. Identity is the SET of records, order-independent — the same
// set of ids always hashes the same, a different set (even by one record) does
// not. Used to decide whether a stored spanning answer reflects the current
// search.
export function fingerprintRecordIds(ids: string[]): string {
  const uniq = [...new Set(ids)].sort();
  const s = uniq.join("\n");
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, "0");
}

// A stored answer reflects the current scope ONLY when both fingerprints exist
// and are equal. A different set, a legacy row with no stored fingerprint, or
// no active search → it's a PREVIOUS answer and must be bannered, never shown
// under the current-scope banner.
export function storedReflectsCurrent(
  storedFingerprint: string | null | undefined,
  currentFingerprint: string | null | undefined,
): boolean {
  return (
    !!storedFingerprint &&
    !!currentFingerprint &&
    storedFingerprint === currentFingerprint
  );
}

// Defensive render for pre-truncation-fix stored rows: if the answer ends in an
// unclosed "[rec:" fragment (the pre-fix silent-truncation artifact), strip it
// and flag truncation so the restored answer renders clean + bannered. Mirrors
// the backend stripDanglingCitation exactly.
export function stripDanglingCitation(text: string): {
  text: string;
  stripped: boolean;
} {
  const idx = text.lastIndexOf("[rec:");
  if (idx === -1) return { text, stripped: false };
  if (text.indexOf("]", idx) !== -1) return { text, stripped: false }; // closed
  return { text: text.slice(0, idx).replace(/\s+$/, ""), stripped: true };
}
